import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AppDispatch, RootState } from "./store";
import { checkAuth, fetchUserInfo } from "./store/slices/authSlice";

// Layouts
import DashboardLayout from "./components/dashboard/DashboardLayout";

// Pages - Auth (public marketing site now lives in the separate Astro `landing/` app)
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import LoginPage from "./pages/LoginPage";

// Pages - Dashboard
import { AppInfoForm } from "./components/appinfo/AppInfoForm";
import { AppInfoList } from "./components/appinfo/AppInfoList";
import { AppInfoBulkEdit } from "./components/appinfo/AppInfoBulkEdit";
import CacheManagementPage from "./pages/admin/CacheManagementPage";
import ConfigurationList from "./pages/configurations/ConfigurationList";
import DashboardPage from "./pages/dashboard/DashboardPage";
import DeviceListPage from "./pages/dashboard/device/DeviceListPage";
import RegisterDevicePage from "./pages/dashboard/device/RegisterDevicePage";
import ProfilePage from "./pages/dashboard/profile/ProfilePage";
import SalesListPage from "./pages/dashboard/sales/SalesListPage";
import SalesReportPage from "./pages/dashboard/sales/SalesReportPage";
import SellTokenPage from "./pages/dashboard/sales/SellTokenPage";
import ManageReferencesPage from "./pages/dashboard/seller/ManageReferencesPage";
import SellerFormPage from "./pages/dashboard/seller/SellerFormPage";
import SellerListPage from "./pages/dashboard/seller/SellerListPage";
import BlockedIpsList from "./pages/security/BlockedIpsList";
import BlockedUsersList from "./pages/security/BlockedUsersList";

// Components
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { LoadingOverlay } from "./components/common/LoadingOverlay";
import ManageSellerPage from "./pages/dashboard/seller/ManageSellerPage";
import TokenDistributionPage from "./pages/dashboard/seller/ShareTokenPage";
import TokenDistributionListPage from "./pages/dashboard/seller/TokenDistributionListPage";
import UploadApkPage from "./pages/dashboard/app/UploadApkPage";
import { App as AntdApp, ConfigProvider } from "antd";

const BRAND = "Shilpon Locker";

// Per-route browser tab titles — consistent "Shilpon Locker — <description>".
const PAGE_TITLES: Record<string, string> = {
  "/login": "Login",
  "/forgot-password": "Forgot Password",
  "/dashboard": "Dashboard",
  "/dashboard/profile": "Profile",
  "/dashboard/seller/add": "Add Seller",
  "/dashboard/seller/list": "Sellers",
  "/dashboard/seller/manage-reference": "Manage References",
  "/dashboard/seller/distribute-token": "Distribute Token",
  "/dashboard/seller/distribution-list": "Token Distribution History",
  "/dashboard/seller/manage": "Manage Seller",
  "/dashboard/sales/sell-token": "Sell Token",
  "/dashboard/sales/list": "Sales",
  "/dashboard/sales/report": "Sales Report",
  "/dashboard/device/register": "Register Device",
  "/dashboard/device/list": "Devices",
  "/dashboard/configurations": "Configurations",
  "/dashboard/app-info/add": "Add App Info",
  "/dashboard/app-info/list": "App Info",
  "/dashboard/app-info/bulk-edit": "Bulk Edit App Info",
  "/dashboard/app/upload": "Upload APK",
  "/dashboard/cache/cache-management": "Cache Management",
  "/dashboard/security/blocked-ips": "Blocked IPs",
  "/dashboard/security/blocked-users": "Blocked Users"
};

const usePageTitle = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const path = pathname.replace(/\/+$/, "") || "/login";
    const desc = PAGE_TITLES[path] ?? "Dashboard";
    document.title = `${BRAND} — ${desc}`;
  }, [pathname]);
};

const App: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated, isLoading } = useSelector(
    (state: RootState) => state.auth
  );

  usePageTitle();

  useEffect(() => {
    dispatch(checkAuth());
  }, [dispatch]);

  // Fetch user profile when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchUserInfo())
        .unwrap()
        .then(() => {
          console.log("Login successful");
        })
        .catch(error => {
          console.error("Failed to load user profile:", error);
        });
    }
  }, [isAuthenticated, dispatch]);

  if (isLoading) {
    return <LoadingOverlay />;
  }

  return (
    <ConfigProvider
      theme={{
        token: {
          fontFamily: "Product Sans"
        }
      }}
    >
      <AntdApp>
      <Routes>
          {/* Auth Routes (marketing pages moved to the Astro landing site) */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="seller">
              <Route path="add" element={<SellerFormPage />} />
              <Route path="list" element={<SellerListPage />} />
              <Route path="manage-reference" element={<ManageReferencesPage />} />
              <Route path="distribute-token" element={<TokenDistributionPage />} />
              <Route path="distribution-list" element={<TokenDistributionListPage />} />
              <Route path="manage" element={<ManageSellerPage />} />
            </Route>
            <Route path="sales">
              <Route path="sell-token" element={<SellTokenPage />} />
              <Route path="list" element={<SalesListPage />} />
              <Route path="report" element={<SalesReportPage />} />
            </Route>
            <Route path="device">
              <Route path="register" element={<RegisterDevicePage />} />
              <Route path="list" element={<DeviceListPage />} />
            </Route>
            <Route path="configurations" element={<ConfigurationList />} />
            <Route path="app-info">
              <Route path="add" element={<AppInfoForm />} />
              <Route path="list" element={<AppInfoList />} />
              <Route path="bulk-edit" element={<AppInfoBulkEdit />} />
            </Route>
            <Route path="app/upload" element={<UploadApkPage />} />
            <Route path="cache/cache-management" element={<CacheManagementPage />} />
            <Route path="security">
              <Route path="blocked-ips" element={<BlockedIpsList />} />
              <Route path="blocked-users" element={<BlockedUsersList />} />
            </Route>
          </Route>

          {/* Root + fallback: dashboard when authenticated, otherwise login */}
          <Route
            path="*"
            element={
              <Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />
            }
          />
      </Routes>
      </AntdApp>
    </ConfigProvider>
  );
};

export default App;
